import * as libraryUtil from "../lib/libraryUtil";
import binderTabIsDirty from "../lib/binderTabIsDirty";
import find from "lodash.find";
import getSortedLibraryNames from "../lib/getSortedLibraryNames";
import { createSelector } from "reselect";

const EMPTY_OBJECT = {};
const EMPTY_ARRAY = [];

export function createGetTargets() {
	return createSelector(
		state => state.targets,
		targets => Object.keys(targets).map(name => targets[name]),
	);
}

export function createGetLibraryTargets() {
	const getTargets = createGetTargets();

	return createSelector(
		(state, libraryName) => libraryName,
		getTargets,
		(libraryName, targets) => {
			return targets.filter(t => t.library_name === libraryName);
		},
	);
}

export function createGetActiveBinder() {
	return createSelector(
		state => state.ui.binderId,
		state => state.binders,
		(binderId, binders) => {
			return binderId !== null && binderId !== undefined ? binders[binderId] : null;
		},
	);
}

export function createGetActiveBinderTab() {
	return createSelector(
		state => state.ui.binderTabId,
		state => state.binderTabs,
		(tabId, binderTabs) => {
			return tabId !== null && tabId !== undefined ? binderTabs[tabId] : null;
		},
	);
}

export function createGetActiveBinderTabs() {
	const getActiveBinder = createGetActiveBinder();

	return createSelector(
		getActiveBinder,
		state => state.binderTabs,
		(activeBinder, binderTabs) => {
			return activeBinder && activeBinder.tabIds
				? activeBinder.tabIds.map(tid => binderTabs[tid])
				: [];
		},
	);
}

export function createGetActiveBinderObservers() {
	const getActiveBinder = createGetActiveBinder();

	return createSelector(
		getActiveBinder,
		state => state.binderObservers,
		(activeBinder, binderObservers) => {
			return activeBinder ? activeBinder.observerIds.map(id => binderObservers[id]) : [];
		},
	);
}

export function createGetActiveBinderDirty() {
	const getActiveBinder = createGetActiveBinder();
	const getActiveBinderTabs = createGetActiveBinderTabs();

	return createSelector(
		getActiveBinder,
		getActiveBinderTabs,
		(activeBinder, activeBinderTabs) => {
			if (!activeBinder) {
				return null;
			}

			return (
				activeBinder.dirty || find(activeBinderTabs, t => binderTabIsDirty(t)) !== undefined
			);
		},
	);
}

export function createGetCurrentUser() {
	return createSelector(
		state => state.session,
		session => {
			if (!session.currentUser) {
				return null;
			}

			return session.currentUser;
		},
	);
}

export function createGetCurrentUserContent() {
	return createSelector(
		state => state.session,
		session => {
			if (!session.currentUser) {
				return null;
			}

			return session.currentUser &&
				session.currentUser.content &&
				(!Array.isArray(session.currentUser.content) || session.currentUser.content.length)
				? session.currentUser.content
				: EMPTY_OBJECT;
		},
	);
}

export function createGetBinderObservers() {
	return createSelector(
		state => state.binderObservers,
		(state, binderId) =>
			state.binders[binderId] ? state.binders[binderId].observerIds : EMPTY_ARRAY,
		(binderObservers, observerIds) => observerIds.map(oId => binderObservers[oId]),
	);
}

export function createGetActiveBinderTabFields() {
	return createSelector(
		state =>
			state.binderTabs[state.ui.binderTabId] && state.binderTabs[state.ui.binderTabId].search
				? state.binderTabs[state.ui.binderTabId].search.target_name
				: null,
		state => state.targets,
		state => state.fields,
		(targetName, targets, fields) => {
			const target = targetName ? targets[targetName] : null;
			return target ? target.fieldIds.map(id => fields[id]) : EMPTY_ARRAY;
		},
	);
}

export function createGetTargetJoinpaths() {
	return createSelector(
		(state, targetName) => state.targets[targetName],
		state => state.joinpaths,
		(target, joinpaths) => (target ? target.joinpathIds.map(id => joinpaths[id]) : EMPTY_ARRAY),
	);
}

export function createGetBinderTabs() {
	return createSelector(
		state => state.binderTabs,
		(state, binderId) =>
			state.binders[binderId] && state.binders[binderId].tabIds
				? state.binders[binderId].tabIds
				: [],
		(binderTabs, tabIds) => tabIds.map(tid => binderTabs[tid]),
	);
}

export function createGetTabTarget() {
	return createSelector(
		state => state.targets,
		(state, tabId) => state.binderTabs[tabId],
		(targets, tab) => {
			const targetName = !!tab && !!tab.search && tab.search.target_name;
			return targetName ? targets[targetName] : null;
		},
	);
}

export function createGetActiveLibraryName() {
	const getActiveBinder = createGetActiveBinder();

	return createSelector(
		getActiveBinder,
		state => state.session.currentUser,
		(_, routeLibrary) => routeLibrary,
		(binder, user, routeLibrary) => {
			if (routeLibrary) {
				return routeLibrary;
			}

			if (binder && binder.libraryName) {
				return binder.libraryName;
			}

			if (user.practice_areas) {
				const activeLibraryNames = getSortedLibraryNames(
					user.practice_areas.filter(p => p.active),
				);
				if (activeLibraryNames.length) {
					return activeLibraryNames[0];
				}
			}

			return null;
		},
	);
}

export function createGetBinderDirty() {
	const getBinderTabs = createGetBinderTabs();

	return createSelector(
		(state, binderId) => state.binders[binderId],
		getBinderTabs,
		(binder, binderTabs) => {
			return binder
				? binder.dirty || find(binderTabs, t => binderTabIsDirty(t)) !== undefined
				: false;
		},
	);
}

export function createGetActiveGroupUsers() {
	return createSelector(
		state => state.groups,
		state => state.users,
		state => state.session.currentUser,
		(groups, users, currentUser) => {
			if (
				!currentUser ||
				!groups[currentUser.group_id] ||
				!groups[currentUser.group_id].userIds
			) {
				return null;
			}

			return groups[currentUser.group_id].userIds.map(id => users[id]);
		},
	);
}

export function createGetGroupUsers() {
	return createSelector(
		state => state.groups,
		state => state.users,
		(_, groupId) => groupId,
		(groups, users, groupId) =>
			groups[groupId] && groups[groupId].userIds
				? groups[groupId].userIds.map(id => users[id])
				: null,
	);
}

export function createGetGroupLibrary() {
	return createSelector(
		state => state.groups,
		(_, groupId) => groupId,
		(_, groupId, libraryName) => libraryName,
		(groups, groupId, libraryName) => {
			if (!groups[groupId] || !groups[groupId].libraries) {
				return null;
			}
			const library = find(groups[groupId].libraries, lib => lib.name === libraryName);
			return library;
		},
	);
}

export function createGetGroupLibraries() {
	return createSelector(
		state => state.groups,
		(_, groupId) => groupId,
		(groups, groupId) => (groups[groupId] ? groups[groupId].libraries : null),
	);
}

export function createGetActiveGroup() {
	return createSelector(
		state => state.groups,
		state => state.session.currentUser,
		(groups, currentUser) => {
			if (!currentUser || !groups[currentUser.group_id]) {
				return null;
			}

			return groups[currentUser.group_id];
		},
	);
}

export function createGetActiveGroupServiceLocked() {
	const getActiveGroup = createGetActiveGroup();
	return createSelector(
		getActiveGroup,
		(state, service) => service,
		(state, service, libraryName) => libraryName,
		(group, service, libraryName) => {
			if (!service || !libraryName || !group || !group.libraries) {
				return true;
			}
			const groupLib = find(group.libraries, lib => lib.name === libraryName);
			if (!groupLib) {
				return true;
			}
			return !groupLib[service];
		},
	);
}

export function createGetSearchTabs() {
	return createSelector(
		state => state.searchTabs,
		(_, search) => search.tabIds,
		(searchTabs, tabIds) => tabIds.map(id => searchTabs[id]),
	);
}

export function createGetReduxSearch() {
	return createSelector(
		state => state.search,
		state => state.reportSearch,
		(_, searchId) => searchId,
		(targetSearches, reportSearches, searchId) => {
			if (!searchId) {
				return null;
			}
			const targetSearch = targetSearches[searchId];
			if (targetSearch) {
				return targetSearch;
			}

			const reportSearch = reportSearches[searchId];
			if (reportSearch) {
				return reportSearch;
			}

			return null;
		},
	);
}

export function createGetDashboards() {
	return createSelector(
		state => state.targets,
		(_, targetName) => targetName,
		(_, __, searchDashboards) => searchDashboards,
		(targets, targetName, searchDashboards) => {
			if (searchDashboards) {
				return searchDashboards;
			}
			const target = targets[targetName];
			return target ? target.dashboards : undefined;
		},
	);
}

export function createGetTileSectionData() {
	return createSelector(
		(state, sectionId) => (sectionId ? state.tileSections[sectionId] : null),
		state => state.tiles,
		(section, tiles) => {
			if (!section) {
				return null;
			}
			let sectionTiles = section.tileIds;
			if (section.tileIds && section.tileIds.length) {
				sectionTiles = section.tileIds.map(id => tiles[id]);
			}
			return {
				...section,
				tiles: sectionTiles,
			};
		},
	);
}

export function createGetBinderTabTiles() {
	return createSelector(
		(state, binderTabId) => state.binderTabs[binderTabId],
		state => state.tileSections,
		state => state.tiles,
		(binderTab, tileSections, tiles) => {
			if (!binderTab || !binderTab.sectionIdNames) {
				return undefined;
			}
			const tabTiles = [];
			binderTab.sectionIdNames.forEach(idName => {
				const section = idName.id ? tileSections[idName.id] : null;
				if (section) {
					section.tileIds.forEach(id => tabTiles.push(tiles[id]));
				}
			});
			return tabTiles;
		},
	);
}

export function createGetRelatedSearchTargets() {
	return createSelector(
		state => state.search,
		state => state.targets,
		(state, searchId) => searchId,
		(searches, targets, searchId) => {
			const search = searches[searchId];
			return search
				? search.related.map(item => ({
						related: item,
						target: targets[item.search.target_name],
				  }))
				: EMPTY_ARRAY;
		},
	);
}

export function createGetRelatedLoading() {
	return createSelector(
		state => state.search,
		(state, searchId) => searchId,
		(searches, searchId) => {
			const search = searches[searchId];
			return !!search && !!search.relatedLoadingId;
		},
	);
}

export function createGetCommonLibraries() {
	return createSelector(
		state => state.library,
		libraries => {
			const libArr = Object.keys(libraries).map(key => libraries[key]);
			return libraryUtil.orderLibraries(libArr);
		},
	);
}

export function createGetLibraryTitle() {
	return createSelector(
		state => state.library,
		(state, libraryName) => libraryName,
		(libraries, libraryName) => (libraries[libraryName] ? libraries[libraryName].title : ""),
	);
}

export function createGetLibrary() {
	return createSelector(
		state => state.library,
		(state, libraryName) => libraryName,
		(libraries, libraryName) => libraries[libraryName],
	);
}
